import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import componentStyles from './fil-chip-group.styles.js';
/**
 * @tag fil-chip-group
 * @property {"primary"|"secondary"} [appearance="primary"] Appearance of the component
 * @property {string} [name=""] Name of the component
 * @property {"standard"|"small"} [size="standard"] Size of the component
 * @property {"input"|"singleselect"|"multiselect"} [variation='input'] Variation of the component
 */
export class FilChipGroup extends LitElement {
  static properties = {
    appearance: { type: String }, // 'primary' | 'secondary'
    name: { type: String },
    size: { type: String }, // "large" | "standard" | "small" | "xsmall"
    variation: { type: String }, // "input" | "singleselect" | "multiselect"
  };

  static styles = [componentStyles];

  get slottedChildren() {
    return this.shadowRoot.querySelector('slot').assignedElements({ flatten: true });
  }

  constructor() {
    super();
    this.appearance = 'primary';
    this.size = 'standard';
    this.variation = 'input';

    if (this.variation === 'singleselect') {
      this.checked = '';
    }
  }

  // will be called if a chip in the same group has variation 'singleselect' and changes its state
  // to 'checked'. it will remove the 'checked' state for all chips in that group.
  onSelectGdsChipSingleselect(event) {
    if (event.target === this) {
      return;
    }

    event.stopPropagation();
    const { detail } = event;

    // ignore if it's the same one
    if (this.checked === detail.value) {
      return;
    }

    this.checked = detail.value;

    // change state on all children accordingly
    this.slottedChildren.forEach(chip => {
      if (this.checked === chip.value) {
        chip.setAttribute('checked', '');
      } else {
        chip.removeAttribute('checked');
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change.singleselect.gds.chip', this.onSelectGdsChipSingleselect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change.singleselect.gds.chip', this.onSelectGdsChipSingleselect);
  }

  firstUpdated(changes) {
    super.firstUpdated(changes);

    if (this.variation === 'singleselect') {
      this.slottedChildren.forEach(chip => {
        chip.setAttribute('name', this.name);
      });
    }
  }

  updated(changes) {
    super.updated(changes);

    if (changes.has('appearance')) {
      this.slottedChildren.forEach(chip => {
        if (this.appearance) {
          chip.setAttribute('appearance', this.appearance);
        } else {
          chip.removeAttribute('appearance');
        }
      });
    }

    if (changes.has('size')) {
      this.slottedChildren.forEach(chip => {
        if (this.size) {
          chip.setAttribute('size', this.size);
        } else {
          chip.removeAttribute('size');
        }
      });
    }

    if (changes.has('variation')) {
      this.slottedChildren.forEach(chip => {
        if (this.variation) {
          chip.setAttribute('variation', this.variation);

          // if variation has changed, we need to unselect any chip as selecting is different for each
          // variation or not allowed at all
          if (changes.get('variation') !== undefined) {
            chip.removeAttribute('checked');
          }
        } else {
          chip.removeAttribute('variation');
        }
      });
    }
  }

  render() {
    const classes = {
      [`fil-chip-group--${this.size}`]: this.size !== 'standard',
    };

    return html`<div
      class="fil-chip-group${classMap(classes)}"
      role="listbox"
      aria-multiselectable=${this.variation === 'multiselect'}
      aria-orientation="horizontal"
      aria-label="Chip Group"
    >
      <slot></slot>
    </div>`;
  }
}

export default FilChipGroup;
